import React, { useEffect, useMemo, useState } from 'react';
import CasinoContainer from '../../../components/casino/CasinoContainer';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import CusPageCardContainer from '../../../components/shared/CusPageCardContainer';
import { Box, Stack, TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import CusButton from '../../../components/material-ui/CusButton';
import CusTooltip from '../../../components/material-ui/CusTooltip';
import Table from '../../../components/material-ui/adaptive/Table';
import MyTableBoldCell from '../../../components/material-ui/adaptive/MyTableBoldCell';
import TypographyTitle from '../../../components/material-ui/table/TypographyTitle';
import AllCheckbox from '../../../components/material-ui/AllCheckbox';
import ShowContainer from '../../../components/material-ui/ShowContainer';
import MyTableCell from '../../../components/material-ui/adaptive/MyTableCell';
import TypographyText from '../../../components/material-ui/table/TypographyText';
import CusSiteDateTime from '../../../components/material-ui/CusSiteDateTime';
import CusCheckbox from '../../../components/material-ui/CusCheckbox';
import { Lock } from '@mui/icons-material';
import { API_USERS } from '../../../api/users';
import useToast from '../../../hooks/useToast';

export default function UserIpAddress() {
  const { t } = useTranslation();
  const { successToast } = useToast();
  const { userId } = useParams();
  const [titleNick, setTitleNick] = useState('');
  const title = t('List of IP addresses for user') + ' ' + titleNick;

  const [users, setUsers] = useState([]);
  const [userInfoHash, setUserInfoHash] = useState({});
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!userId) return;
    API_USERS.ipAddress({ userId: +userId })
      .then((e) => {
        const data = e.data?.data;
        const userList = data.userList || {};
        setUsers(data.ipList);
        setUserInfoHash(data.userList);
        setTitleNick(userList[userId][0]);
      })
      .catch(console.log);
  }, [userId]);

  const lockHandler = async (userList, lock) => {
    try {
      await API_USERS.block({ userList, lock });
      setUserInfoHash((prev) => {
        const prevInfo = JSON.parse(JSON.stringify(prev));
        userList.forEach((id) => {
          prevInfo[id][6] = lock;
        });
        return prevInfo;
      });
      lock ? successToast('Selected users are blocked') : successToast('User is unblocked');
    } catch (e) {
      console.log(e);
    }
  };

  const onBlockClick = async () => {
    await lockHandler(selected, true);
    setSelected([]);
  };

  const onUnlock = async (userId) => {
    await lockHandler([userId], false);
    setSelected([]);
  };

  const allUserId = useMemo(() => {
    try {
      const allUsers = [
        ...new Set(users.map((ipInfo) => ipInfo?.users?.map((el) => el[0])).flat())
      ];

      return allUsers.filter((elId) => !userInfoHash[elId]?.[6]);
    } catch (e) {
      return [];
    }
  }, [users, userInfoHash]);

  return (
    <CusPageCardContainer title={title} backInfo={'auto'}>
      <Stack gap={1}>
        <Stack alignSelf={'end'}>
          <Stack gap={1} direction={'row'} alignItems={'center'}>
            <CusButton
              variant="outlined"
              text="Block marked accounts"
              onClick={onBlockClick}
              disabled={!selected.length}
            />
            <CusTooltip text={'The selected player accounts will be blocked.'} />
          </Stack>
        </Stack>
        <TableContainer sx={{ maxHeight: 'calc(100svh - 250px)', overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <MyTableBoldCell isLess>
                  <TypographyTitle>{t('IP addresses')}</TypographyTitle>
                </MyTableBoldCell>
                <MyTableBoldCell isLess>
                  <AllCheckbox
                    checkBoxSx={{ paddingLeft: 0 }}
                    label={'User'}
                    fontWeight={900}
                    allListId={allUserId}
                    chosenList={selected}
                    setChosenList={setSelected}
                  />
                </MyTableBoldCell>
                <MyTableBoldCell>{t('Date')}</MyTableBoldCell>
                <MyTableBoldCell>{t('Deposit')}</MyTableBoldCell>
                <MyTableBoldCell>{t('Withdrawal')}</MyTableBoldCell>
                <MyTableBoldCell>{t('Balance')}</MyTableBoldCell>
                <MyTableBoldCell>{t('Devices')}</MyTableBoldCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((ipInfo, indx) =>
                ipInfo.users.map((user, childInx) => {
                  const userId = user[0];
                  const curUserInfo = userInfoHash[userId] || [];
                  const userLogin = curUserInfo[0];
                  const deposits = curUserInfo[1] || {};
                  const withdrawals = curUserInfo[2] || {};
                  const balances = curUserInfo[3] || {};

                  return (
                    <TableRow
                      key={'ip_row' + indx + childInx}
                      sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                      <ShowContainer condition={!childInx}>
                        <MyTableCell
                          isLess
                          rowSpan={ipInfo.users.length}
                          sx={ipInfo.users.length > 1 && { backgroundColor: 'background.default' }}>
                          <Stack gap={0.5}>
                            <TypographyText isCopy={ipInfo.ipAddress}>
                              {ipInfo.ipAddress}
                            </TypographyText>
                            <TypographyText>
                              <CusSiteDateTime date={ipInfo.dateTime} />
                            </TypographyText>
                          </Stack>
                        </MyTableCell>
                      </ShowContainer>
                      <MyTableCell isLess>
                        <Stack direction={'row'} minWidth={200}>
                          <ShowContainer
                            condition={!curUserInfo[6]}
                            plug={
                              <Box
                                title={t('Click to unlock')}
                                sx={{ cursor: 'pointer' }}
                                pr={1}
                                onClick={() => onUnlock(userId)}>
                                <Lock color="error" />
                              </Box>
                            }>
                            <CusCheckbox
                              checkBoxSx={{ paddingLeft: 0 }}
                              checked={selected.includes(userId)}
                              onChange={() =>
                                setSelected((prev) =>
                                  prev.includes(userId)
                                    ? prev.filter((el) => el != userId)
                                    : [...prev, userId]
                                )
                              }
                            />
                          </ShowContainer>

                          <Stack>
                            {userLogin}
                            <TypographyText>
                              {t('Casino')}: {curUserInfo[5]}
                            </TypographyText>
                          </Stack>
                        </Stack>
                      </MyTableCell>
                      <MyTableCell isLess>
                        <TypographyText>
                          <CusSiteDateTime date={user[2]} />
                        </TypographyText>
                      </MyTableCell>

                      <MyTableCell isLess>
                        <Stack alignItems={'end'}>
                          {Object.keys(deposits).map((currency) => (
                            <TypographyText key={'deposit_' + currency}>
                              {deposits[currency]} {currency}
                            </TypographyText>
                          ))}
                        </Stack>
                      </MyTableCell>
                      <MyTableCell isLess>
                        <Stack alignItems={'end'}>
                          {Object.keys(withdrawals).map((currency) => (
                            <TypographyText key={'withdrawal_' + currency}>
                              {withdrawals[currency]} {currency}
                            </TypographyText>
                          ))}
                        </Stack>
                      </MyTableCell>
                      <MyTableCell isLess>
                        <Stack alignItems={'end'}>
                          {Object.keys(balances).map((currency) => (
                            <TypographyText key={'balance_' + currency}>
                              {balances[currency]} {currency}
                            </TypographyText>
                          ))}
                        </Stack>
                      </MyTableCell>
                      <MyTableCell isLess>
                        <Box maxWidth={320}>
                          <TypographyText sx={{ wordBreak: 'break-all' }}>{user[1]}</TypographyText>
                        </Box>
                      </MyTableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </CusPageCardContainer>
  );
}
